import { useState, useRef, useEffect } from 'react';
import '../App.css';
import axios from 'axios';
import Swal from 'sweetalert2';
import FrameList from './CanvasFloatingFrameList';
import FrameStickSpecPanel from './FrameStickSpecPanel';
import { totalPrice as calculatePrice } from './PricingEngine';
import {
  CUSTOM_SIZE_OPTION,
  customSizeOptions,
  parseCustomDims,
  getImageSizeInches,
  getPrintAreaInches,
  getEffectiveOpeningCm,
  nearestTierForPricing as computeNearestPrintTier,
  computePreviewCanvasSize,
  getCustomSizeMetaString
} from './customPrintSize';

function CanvasPrintandFrame({ image, originalImage, onEditImage }) {
  const userImage = image || localStorage.getItem('opticon_last_image');
  const uploadedUserImage = originalImage || userImage;
  const canvasRef = useRef(null);

  /* ================= STATES ================= */
  const [printType, setPrintType] = useState('canvas');

  const [frameOpen, setFrameOpen] = useState(false);
  const [selectedFrame, setSelectedFrame] = useState(null);

  const [loading, setLoading] = useState(false);
  const [, setProgress] = useState(0);
  const openFrameSidebar = () => setFrameOpen(true);
  const DPI = 300;
  const [imageSizeCm, setImageSizeCm] = useState(null);
  const [customSize, setCustomSize] = useState(CUSTOM_SIZE_OPTION);
  const [customWidthCm, setCustomWidthCm] = useState('');
  const [customHeightCm, setCustomHeightCm] = useState('');
  const [appliedCustomCropKey, setAppliedCustomCropKey] = useState('');

  /* ================= FRAME OPTIONS ================= */
  const frameOptions = FrameList;
  const CM_TO_MM = 10;

  const { widthIn, heightIn, normalizedSize } = getImageSizeInches(imageSizeCm);
  const cmToInches = (cm) => {
    const parsed = parseFloat(cm);
    if (!parsed || parsed <= 0) return 0;

    return parsed / 2.54;
  };
  const customDimsParsed = () => {
    const widthInches = cmToInches(customWidthCm);
    const heightInches = cmToInches(customHeightCm);

    return parseCustomDims(widthInches, heightInches);
  };
  const isCustomWhMode = customSize === CUSTOM_SIZE_OPTION;
  const customDims = isCustomWhMode ? customDimsParsed() : null;
  const customWidthValue = Number(customWidthCm);
  const customHeightValue = Number(customHeightCm);
  const hasValidCustomSize =
    Number.isFinite(customWidthValue) &&
    Number.isFinite(customHeightValue) &&
    customWidthValue > 0 &&
    customHeightValue > 0;
  const customCropKey = hasValidCustomSize
    ? `${customWidthValue}x${customHeightValue}`
    : '';
  const showApplyCustomCrop =
    isCustomWhMode && hasValidCustomSize && customCropKey !== appliedCustomCropKey;

  const handleApplyCustomCrop = () => {
    if (!showApplyCustomCrop || !onEditImage) return;

    setAppliedCustomCropKey(customCropKey);
    onEditImage({
      aspect: customWidthValue / customHeightValue,
      customWidthCm: customWidthValue,
      customHeightCm: customHeightValue
    });
  };
  const effOpeningCm = getEffectiveOpeningCm(customSize, customDims, imageSizeCm);

  const area = getPrintAreaInches(customSize, customDims, widthIn, heightIn);
  const { previewCanvasW, previewCanvasH } = computePreviewCanvasSize(area);

  const nearestTier = computeNearestPrintTier({
    printType,
    customSize,
    customDims,
    widthIn,
    heightIn
  });

  /* ================= PRICES ================= */
  const calculateTotalPrice = () => {
    if (!imageSizeCm) return 0;

    const width =
      Number(effOpeningCm.widthCm) > 0
        ? effOpeningCm.widthCm
        : Number(imageSizeCm.widthCm);
    const height =
      Number(effOpeningCm.heightCm) > 0
        ? effOpeningCm.heightCm
        : Number(imageSizeCm.heightCm);

    /* ================= ENGINE PRICE ================= */
    const pricingResult = calculatePrice({
      qty: 1,
      opening_width: width * CM_TO_MM,
      opening_height: height * CM_TO_MM,
      print_type: printType,
      frame: selectedFrame || {},
      mat: {},
      glass: {},
      backing: {},
      extras: [],
      openings: 1,
      discount: 0
    });

    return pricingResult.finalPrice;
  };
  const totalPrice = calculateTotalPrice();
  /* ===== GST (10%) ===== */
  const gstAmount = totalPrice * 0.10;
  const totalWithGST = totalPrice + gstAmount;
  /* ================= FILTER ================= */

  const getPrintFilter = () => {
    switch (printType) {
      case 'canvas': return 'contrast(1.05) saturate(1.1)';
      case 'archival': return 'contrast(1.05) saturate(0.95)';
      case 'art-decor': return 'contrast(0.95) saturate(0.9) brightness(0.98)';
      default: return 'none';
    }
  };

  /* ================= CANVAS ENGINE ================= */

  const renderCanvas = async () => {
    if (!userImage || !canvasRef.current) return;

    const canvas = canvasRef.current;
    const ctx = canvas.getContext('2d');

    /* ========================================================
       LOAD USER IMAGE
    ======================================================== */

    const img = new Image();
    img.crossOrigin = 'anonymous';
    img.src = userImage;

    await new Promise((resolve, reject) => {
      img.onload = resolve;
      img.onerror = reject;
    });

    const W = canvas.width;
    const H = canvas.height;

    /* ========================================================
       CLEAR CANVAS
    ======================================================== */

    ctx.clearRect(0, 0, W, H);


    /* ========================================================
       FRAME THICKNESS
    ======================================================== */

    const frameThickness = selectedFrame
      ? Number(selectedFrame.thickness) || 0
      : 0;


    /* ========================================================
       IMAGE AREA
    ======================================================== */

    const innerX = frameThickness;
    const innerY = frameThickness;

    const innerW = W - frameThickness * 2;
    const innerH = H - frameThickness * 2;


    /* ========================================================
       DRAW IMAGE
    ======================================================== */

    ctx.filter = getPrintFilter();

    ctx.drawImage(
      img,
      innerX,
      innerY,
      innerW,
      innerH
    );

    ctx.filter = 'none';


    /* ========================================================
       DRAW FRAME
    ======================================================== */

    if (selectedFrame && frameThickness > 0) {

      const frameImg = new Image();
      frameImg.crossOrigin = 'anonymous';
      frameImg.src = selectedFrame.img;

      await new Promise((resolve, reject) => {
        frameImg.onload = resolve;
        frameImg.onerror = reject;
      });


      /*
        Miter controls the 45° cut
        where the frame mouldings meet.
      */
      const miter = frameThickness;


      /* ======================================================
         TOP FRAME
         Normal moulding direction
      ====================================================== */

      ctx.save();

      ctx.beginPath();

      // Outer top-left
      ctx.moveTo(0, 0);

      // Outer top-right
      ctx.lineTo(W, 0);

      // Inner top-right 45° miter
      ctx.lineTo(W - miter, frameThickness);

      // Inner top-left 45° miter
      ctx.lineTo(miter, frameThickness);

      ctx.closePath();

      ctx.clip();

      ctx.drawImage(
        frameImg,
        0,
        0,
        W,
        frameThickness
      );

      ctx.restore();


      /* ======================================================
         BOTTOM FRAME
         180° OPPOSITE OF TOP FRAME
         
         The moulding/profile is flipped toward
         the image, matching the top frame from
         the opposite direction.
      ====================================================== */

      ctx.save();

      ctx.beginPath();

      // Inner bottom-left 45° miter
      ctx.moveTo(miter, H - frameThickness);

      // Inner bottom-right 45° miter
      ctx.lineTo(W - miter, H - frameThickness);

      // Outer bottom-right
      ctx.lineTo(W, H);

      // Outer bottom-left
      ctx.lineTo(0, H);

      ctx.closePath();

      ctx.clip();

      /*
        Rotate the SAME frame texture 180°.
        This makes the bottom moulding opposite
        to the top moulding.
      */
      ctx.translate(W, H);
      ctx.rotate(Math.PI);

      ctx.drawImage(
        frameImg,
        0,
        0,
        W,
        frameThickness
      );

      ctx.restore();


      /* ======================================================
         LEFT FRAME
         Vertical moulding
         45° miter at top and bottom joints
      ====================================================== */

      ctx.save();

      ctx.beginPath();

      // Outer top-left
      ctx.moveTo(0, 0);

      // Inner top-left 45° miter
      ctx.lineTo(frameThickness, miter);

      // Inner bottom-left 45° miter
      ctx.lineTo(frameThickness, H - miter);

      // Outer bottom-left
      ctx.lineTo(0, H);

      ctx.closePath();

      ctx.clip();

      /*
        Rotate the same frame texture
        90° counter-clockwise.
      */
      ctx.translate(0, H);
      ctx.rotate(-Math.PI / 2);

      ctx.drawImage(
        frameImg,
        0,
        0,
        H,
        frameThickness
      );

      ctx.restore();


      /* ======================================================
         RIGHT FRAME
         Vertical moulding
         45° miter at top and bottom joints
      ====================================================== */

      ctx.save();

      ctx.beginPath();

      // Inner top-right 45° miter
      ctx.moveTo(W - frameThickness, miter);

      // Outer top-right
      ctx.lineTo(W, 0);

      // Outer bottom-right
      ctx.lineTo(W, H);

      // Inner bottom-right 45° miter
      ctx.lineTo(W - frameThickness, H - miter);

      ctx.closePath();

      ctx.clip();

      /*
        Rotate the same frame texture
        90° clockwise.
      */
      ctx.translate(W, 0);
      ctx.rotate(Math.PI / 2);

      ctx.drawImage(
        frameImg,
        0,
        0,
        H,
        frameThickness
      );

      ctx.restore();
    }
  };

  useEffect(() => {
    renderCanvas();
  }, [printType, selectedFrame, userImage, customSize, customWidthCm, customHeightCm, imageSizeCm]);

  const exportEditedImage = () => {
    return canvasRef.current.toDataURL('image/png');
  };

  /* ================= ADD TO CART ================= */
  const handleAddToCart = async () => {

    if (!exportEditedImage) {
      alert("Image not ready.");
      return;
    }

    if (isCustomWhMode && !hasValidCustomSize) {
      Swal.fire({
        icon: 'warning',
        title: 'Print Size Required',
        text: 'Please enter the print size width and height in cm.'
      });
      return;
    }

    setLoading(true);
    setProgress(0);

    try {
      /* ================================
         IMAGE → BASE64 (REAL PIPELINE)
      ================================ */

      const imageDataUrl = exportEditedImage(); // returns dataURL/base64

      // convert base64 → blob
      const resImg = await fetch(userImage);
      const blob = await resImg.blob();

      const editedImageBase64 = await new Promise((resolve) => {
        const reader = new FileReader();
        reader.onloadend = () => resolve(reader.result);
        reader.readAsDataURL(blob);
      });

      /* ================================
         PAYLOAD
      ================================ */

      const payload = {
        id: 8432,
        quantity: 1,
        meta_data: [
          { key: 'print_type', value: printType },
          {
            key: 'custom_size',
            value: getCustomSizeMetaString(
              normalizedSize,
              customSize,
              customDimsParsed()
            )
          },
          {
            key: 'image_size',
            value: `${imageSizeCm.widthCm} × ${imageSizeCm.heightCm} cm`
          },

          {
            key: 'custom_size',
            value: getCustomSizeMetaString(
              normalizedSize,
              customSize,
              customDimsParsed()
            )
          },

          {
            key: 'print_size',
            value: nearestTier.key
          },
          { key: 'print_nearest_tier', value: nearestTier.key },
          { key: 'frame_name', value: selectedFrame?.name || 'No Frame' },
          { key: 'frame_price', value: selectedFrame?.price || 0 },
          { key: 'total_price', value: totalWithGST },
          { key: 'edited_image', value: editedImageBase64 },
          { key: 'user-uploaded-image', value: uploadedUserImage }
        ]
      };


      console.log('ADD TO CART PAYLOAD:', payload);

      /* ================================
         API CALL
      ================================ */

      const response = await axios.post(
        `https://opticonframing.com.au/wp-json/opticon/v1/canvas-print-and-frame/add-to-cart`,
        payload,
        {
          headers: { 'Content-Type': 'application/json' },
          withCredentials: true,
          onUploadProgress: (e) => {
            if (!e.total) return;
            const percent = Math.round((e.loaded * 100) / e.total);
            setProgress(percent);   // ✅ real dynamic %
          },
        }
      );

      /* ================================
         RESPONSE
      ================================ */

      if (response.data?.success) {
        setProgress(100);

        setTimeout(() => {
          window.location.href = 'https://opticonframing.com.au/cart';
        }, 700);

      } else {
        throw new Error(response.data?.error || 'Server error');
      }

    } catch (err) {
      console.error('❌ Add to cart error:', err);
      alert('Failed to add to cart. Check console for details.');
      setLoading(false);
      setProgress(0);
    }
  };

  useEffect(() => {
    if (!userImage) return;

    const img = new Image();
    img.onload = () => {
      const widthCm = ((img.width / DPI) * 2.54).toFixed(1);
      const heightCm = ((img.height / DPI) * 2.54).toFixed(1);

      setImageSizeCm({
        widthCm,
        heightCm,
        widthPx: img.width,
        heightPx: img.height
      });
    };

    img.src = userImage;
  }, [userImage]);

  useEffect(() => {
    if (!selectedFrame && frameOptions.length > 0) {
      setSelectedFrame(frameOptions[0]);
    }
  }, [selectedFrame, frameOptions]);

  /* ================= UI ================= */

  return (
    <div className="product-page">

      {loading && (
        <div style={{
          position: 'fixed', top: 0, left: 0, width: '100%', height: '100%',
          backgroundColor: 'rgba(0,0,0,0.8)', display: 'flex', flexDirection: 'column',
          justifyContent: 'center', alignItems: 'center', zIndex: 9999
        }}>
          {/* Circular Loader */}
          <div style={{
            width: '60px', height: '60px', borderRadius: '50%',
            border: '5px solid rgba(255,255,255,0.1)',
            borderTop: '5px solid #1E5FFF', // Requested Color
            animation: 'spin 1s linear infinite',
            marginBottom: '20px'
          }}></div>

          {/* Percentage Text */}
          <p style={{ color: 'white', fontSize: '24px', fontWeight: 'bold' }}>
            Loading...
          </p>

          <style>{`
            @keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }
          `}</style>
        </div>
      )}

      {/* LEFT PREVIEW */}
      <div className="product-image-panel">
        <canvas
          ref={canvasRef}
          width={previewCanvasW}
          height={previewCanvasH}
          className="real-canvas-preview"
        />
      </div>

      {/* RIGHT PANEL */}
      <div className="product-info-panel">

        <h1 className="product-title">Canvas Print & Frame</h1>

        {/* Image Size Info */}
        {imageSizeCm && (
          <div className="cpfimage-size-row">
            <span className="form-label">Image Size</span>
            <span className="cpfimage-size-value">
              {imageSizeCm.widthCm}×{imageSizeCm.heightCm} cm
            </span>

            <button
              type="button"
              className="edit-image-btn"
              onClick={() => onEditImage?.()}
              title="Edit image"
            >
              <i class="fa-solid fa-pen-to-square"></i>
            </button>
          </div>
        )}

        {/* Custom Size */}
        <div className="print-option-row">
          <label className="form-label">Custom Size</label>

          <select
            className="form-select"
            value={customSize}
            onChange={(e) => setCustomSize(e.target.value)}
          >
            <option value={CUSTOM_SIZE_OPTION}>
              Custom width × height (cm)
            </option>

            <option value="">
              Auto (Based on image size)
            </option>


            {customSizeOptions.map((opt) => (
              <option key={opt.label} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        {isCustomWhMode && (
          <div className="print-size-canvas-block">
            <label className="form-label print-size-canvas-block__title">
              Print Size
            </label>

            <div className="print-size-canvas-block__row">

              <div className="print-size-canvas-block__field">
                <span
                  className="print-size-canvas-block__dim-label"
                  id="print-canvas-w-label"
                >
                  W
                </span>

                <input
                  id="print-canvas-w-cm"
                  type="number"
                  className="print-size-canvas-block__input"
                  min={1}
                  step={0.1}
                  placeholder="cm"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-labelledby="print-canvas-w-label"
                  value={customWidthCm}
                  onChange={(e) => setCustomWidthCm(e.target.value)}
                />
              </div>

              <div className="print-size-canvas-block__field">
                <span
                  className="print-size-canvas-block__dim-label"
                  id="print-canvas-h-label"
                >
                  H
                </span>

                <input
                  id="print-canvas-h-cm"
                  type="number"
                  className="print-size-canvas-block__input"
                  min={1}
                  step={0.1}
                  placeholder="cm"
                  inputMode="decimal"
                  autoComplete="off"
                  aria-labelledby="print-canvas-h-label"
                  value={customHeightCm}
                  onChange={(e) => setCustomHeightCm(e.target.value)}
                />
              </div>

            </div>

            {showApplyCustomCrop && (
              <button
                type="button"
                className="print-size-canvas-block__apply"
                onClick={handleApplyCustomCrop}
              >
                Apply
              </button>
            )}
          </div>
        )}

        {/* Frame Selector (Dropdown-style row) */}
        <div
          className="print-option-row frame-row"
        >
          <label className="form-label">Frame</label>

          <div className="frame-dropdown-display" onClick={() => setFrameOpen(true)}>
            <span>
              {selectedFrame ? selectedFrame.name : 'Select Frame'}
            </span>
            <span className="frame-arrow">›</span>
          </div>
        </div>

        {/* Price */}
        <div className="price-cart-row">
          <div className="product-price">${totalPrice.toFixed(2)} + GST (10%) ${gstAmount.toFixed(2)} = ${totalWithGST.toFixed(2)}</div>

          <button
            className="add-to-cart-btn"
            onClick={handleAddToCart}
            disabled={loading}
          >
            {loading ? 'Loading...' : 'Add to Cart'}
          </button>
        </div>

        <FrameStickSpecPanel frame={selectedFrame} />

      </div>

      {/* FRAME SIDEBAR */}
      {frameOpen && (
        <div className="frame-sidebar open" onClick={(e) => e.stopPropagation()}>
          <div className="frame-sidebar-header">
            <h3>Frames</h3>
            <button onClick={() => setFrameOpen(false)}>✕</button>
          </div>

          {frameOptions.map(frame => (
            <div
              key={frame.id}
              className={`frame-item ${selectedFrame?.id === frame.id ? 'active' : ''}`}
              onClick={() => {
                setSelectedFrame(frame);   // apply frame
                setFrameOpen(false);       // auto-close
              }}
            >
              <img src={frame.img} alt={frame.name} />
              <div className="frame-meta">
                <div className="frame-name">{frame.name}</div>
              </div>
            </div>
          ))}
        </div>
      )}


    </div>
  );
}

export default CanvasPrintandFrame;
